import { useEffect } from 'react';
import { useGet } from 'shared/hooks/useFetch';
import {
  TransitionRisk,
  Entity,
  CountriesAndSectors,
  ViewType,
  ExportUrl,
  Scenario,
  RiskEntry,
  TaxEvolution,
  ValueChainDistribution,
  ContributionByCountry,
  Contribution,
} from '../types/climateStressTest';

const apiUrl = '/esg/climate-stress-test';

export const useGetCountriesAndSectors = () =>
  useGet<CountriesAndSectors>(`${apiUrl}/coverage/countries-and-sectors`);

export const useGetEntity = (id: string) => useGet<Entity>(`${apiUrl}/entity/${id}`);

export const useGetExportUrl = (view: ViewType, country: string, sector?: string) => {
  const { data: exportUrl, refetch } = useGet<ExportUrl>(`${apiUrl}/dashboard/${view}/export`, {
    params: { country, sector },
    disableAutofetch: true,
  });

  useEffect(() => {
    if (!exportUrl?.fileUrl) {
      return;
    }

    window.location.assign(exportUrl?.fileUrl);
  }, [exportUrl]);

  return refetch;
};

export const useGetTotalOutputLoss = (country: string, sector: string) =>
  useGet<TransitionRisk<RiskEntry>>(`${apiUrl}/dashboard/total-output-loss`, {
    params: { country, sector, page: 1, limit: 1000 },
  });

export const useGetTransitionRisk = (country: string, sector: string) =>
  useGet<TransitionRisk<RiskEntry>>(`${apiUrl}/dashboard/output-loss-from-transition-risk`, {
    params: { country, sector, page: 1, limit: 1000 },
  });

export const useGetCarbonIntensity = (country: string, sector: string) =>
  useGet<TransitionRisk<RiskEntry>>(`${apiUrl}/dashboard/carbon-intensity`, {
    params: { country, sector, page: 1, limit: 1000 },
  });

export const useGetCarbonTaxEvolution = (country: string, scenario: Scenario) =>
  useGet<TransitionRisk<TaxEvolution>>(`${apiUrl}/dashboard/evolution-of-carbon-tax`, {
    params: { country, scenario, page: 1, limit: 1000 },
  });

export const useGetValueChainDistribution = (country: string, sector: string, scenario: Scenario) =>
  useGet<ValueChainDistribution>(
    `${apiUrl}/dashboard/value-chain-distribution-of-output-losses/${country}/${sector}/${scenario}`,
  );

export const useGetPhysicalRisk = (country: string, sector: string) =>
  useGet<TransitionRisk<RiskEntry>>(`${apiUrl}/dashboard/output-loss-from-physical-risk`, {
    params: { country, sector, page: 1, limit: 1000 },
  });

export const useGetContributionByCountry = (country: string, scenario: Scenario) =>
  useGet<ContributionByCountry>(
    `${apiUrl}/dashboard/contribution-to-temperature-change-by-country/${country}/${scenario}`,
  );

export const useGetContribution = (country: string, scenario: Scenario) =>
  useGet<TransitionRisk<Contribution>>(`${apiUrl}/dashboard/contribution-to-temperature-change`, {
    params: { country, scenario, page: 1, limit: 1000 },
  });

export const useGetAvailability = () => {
  const { error, loading } = useGet<TransitionRisk<RiskEntry>>(
    `${apiUrl}/dashboard/total-output-loss`,
    { params: { page: 1, limit: 1 } },
  );

  return { data: loading ? true : error?.code !== 403, loading };
};
